import { Suspense, useCallback, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { getComponent, getManifest, resolveConfig, type GameResult } from '@senses/games'

import { ChunkBoundary } from '../components/ChunkBoundary.tsx'
import { LevelLadder, ParamSummary } from '../components/ParamForm.tsx'
import { TrialStrip } from '../components/ProgressChart.tsx'
import { Alert, Spinner } from '../components/ui.tsx'
import { api, type TrainingSession } from '../lib/api.ts'
import { useAction, useAsync } from '../lib/hooks.ts'
import { formatDuration, formatNumber } from '../lib/format.ts'

type Stage = 'intro' | 'playing' | 'result'

/** Kratší přerušenou sérii nemá smysl zaznamenávat. */
const MIN_TRIALS_TO_RECORD = 3

/**
 * Hrací obrazovka. `byProgram` znamená, že se hraje program přímo —
 * to používá trenér při zkoušení nastavení, výsledek se pak neváže
 * na žádné přiřazení.
 */
export function PlayPage({ byProgram }: { byProgram?: boolean }): React.JSX.Element {
  const params = useParams()
  const navigate = useNavigate()
  const [stage, setStage] = useState<Stage>('intro')
  const [saved, setSaved] = useState<TrainingSession | null>(null)
  const [result, setResult] = useState<GameResult | null>(null)

  const id = Number(byProgram ? params.programId : params.assignmentId)

  const source = useAsync(
    async () =>
      byProgram
        ? { program: (await api.programs.get(id)).program, assignmentId: null }
        : await (async () => {
            const { assignment } = await api.assignments.get(id)
            return { program: assignment.program, assignmentId: assignment.id }
          })(),
    [id, byProgram],
  )

  const program = source.data?.program ?? null
  const manifest = program ? getManifest(program.gameId) : undefined
  const GameComponent = program ? getComponent(program.gameId) : undefined

  // Uložená konfigurace se doplní o defaulty, aby fungovala i po přidání
  // nového parametru do hry.
  const config = useMemo(
    () => (manifest ? resolveConfig(manifest, program?.params) : null),
    [manifest, program?.params],
  )

  const save = useAction(async (gameResult: GameResult, completed: boolean) => {
    if (!program) return null
    const { session } = await api.sessions.create({
      programId: program.id,
      gameId: program.gameId,
      params: config ?? {},
      score: gameResult.score,
      accuracy: gameResult.accuracy,
      metrics: gameResult.metrics,
      trials: gameResult.trials,
      durationMs: gameResult.durationMs,
      seed: gameResult.seed,
      completed,
    })
    return session
  })

  const handleFinish = useCallback(
    (gameResult: GameResult) => {
      setResult(gameResult)
      setStage('result')
      void save.run(gameResult, true).then((session) => {
        if (session) setSaved(session)
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const handleAbort = useCallback(
    (partial?: GameResult) => {
      setStage('intro')
      // Pár úvodních pokusů je spíš omyl než trénink; ukládá se až od tří,
      // aby historie nebyla zaplevelená nedopatřeními.
      if (partial && partial.trials.length >= MIN_TRIALS_TO_RECORD) {
        void save.run(partial, false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  /* ---------------- Načítání a chybové stavy ---------------- */

  if (source.loading) {
    return (
      <div className="play">
        <Spinner label="Připravuji trénink…" />
      </div>
    )
  }

  if (source.error || !program) {
    return (
      <div className="play">
        <div className="play__intro">
          <div className="play__card">
            <Alert>{source.error ?? 'Trénink se nepodařilo načíst'}</Alert>
            <button type="button" className="btn" onClick={() => void navigate('/')}>
              Zpět na přehled
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (!manifest || !GameComponent || !config) {
    return (
      <div className="play">
        <div className="play__intro">
          <div className="play__card">
            <h2>Hra není k dispozici</h2>
            <p className="play__desc">
              Program odkazuje na hru <code>{program.gameId}</code>, kterou tato verze aplikace nezná.
              Nejspíš byla odebrána nebo přejmenována.
            </p>
            <button type="button" className="btn" onClick={() => void navigate('/')}>
              Zpět na přehled
            </button>
          </div>
        </div>
      </div>
    )
  }

  /* ---------------- Hra ---------------- */

  if (stage === 'playing') {
    return (
      <div className="play">
        {/* Kód hry se stahuje až tady. Boundary musí být vně Suspense,
            aby zachytila i selhání samotného stažení. */}
        <ChunkBoundary what={`hru „${manifest.name}"`}>
          <Suspense fallback={<Spinner label="Načítám hru…" />}>
            <GameComponent config={config} onFinish={handleFinish} onAbort={handleAbort} />
          </Suspense>
        </ChunkBoundary>
      </div>
    )
  }

  /* ---------------- Výsledek ---------------- */

  if (stage === 'result' && result) {
    return (
      <div className="play">
        <div className="play__intro">
          <div className="play__card">
            <div className="play__icon">{result.accuracy >= 0.5 ? '🎉' : '💪'}</div>
            <h2>Série dokončena</h2>
            {/* Bez názvu programu a nastavení jsou čísla níž nezasazená —
                skill 6,2 se čtyřmi cíli je jiný výkon než se dvěma. */}
            <p className="dim" style={{ fontSize: '0.9rem' }}>
              {program.name}
            </p>

            {/* Metriky se berou z manifestu, takže nová hra ukáže ty svoje
                bez zásahu do této stránky. */}
            <div className="stats" style={{ margin: '1.5rem 0' }}>
              <div className="stat">
                <div className="stat__label">{manifest.primaryMetric.label}</div>
                <div className="stat__value" style={{ color: 'var(--accent-soft)' }}>
                  {formatNumber(result.score, manifest.primaryMetric.precision)}
                </div>
              </div>

              {(manifest.secondaryMetrics ?? []).map((metric) => {
                const value = result.metrics[metric.key]
                if (value === undefined) return null
                return (
                  <div className="stat" key={metric.key}>
                    <div className="stat__label">{metric.label}</div>
                    <div className="stat__value">
                      {formatNumber(value, metric.precision)}
                      {metric.unit && <span className="stat__hint"> {metric.unit}</span>}
                    </div>
                  </div>
                )
              })}

              <div className="stat">
                <div className="stat__label">Čas</div>
                <div className="stat__value">{formatDuration(result.durationMs)}</div>
              </div>
            </div>

            <div style={{ textAlign: 'left', marginBottom: '1.5rem' }}>
              <div className="stat__label" style={{ marginBottom: '0.5rem' }}>
                Průběh série
              </div>
              <TrialStrip trials={result.trials} />
              <p className="field__help" style={{ marginTop: '0.5rem' }}>
                Výška sloupce je rychlost, zelená znamená zvládnutý pokus.
              </p>
            </div>

            <div style={{ textAlign: 'left', marginBottom: '1.5rem' }}>
              <div className="stat__label" style={{ marginBottom: '0.5rem' }}>
                Nastavení tréninku
              </div>
              <ParamSummary manifest={manifest} params={config} />
            </div>

            {save.pending && <p className="muted">Ukládám výsledek…</p>}
            {save.error && <Alert>Výsledek se nepodařilo uložit: {save.error}</Alert>}
            {saved && <Alert kind="success">Výsledek uložen do tvé historie.</Alert>}

            <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'center', marginTop: '1rem' }}>
              <button
                type="button"
                className="btn"
                onClick={() => void navigate(byProgram ? '/trenink' : '/')}
              >
                Hotovo
              </button>
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => {
                  setResult(null)
                  setSaved(null)
                  setStage('playing')
                }}
              >
                Ještě jednou
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  /* ---------------- Úvodní obrazovka ---------------- */

  return (
    <div className="play">
      <div className="play__intro">
        <div className="play__card">
          <div className="play__icon">{manifest.icon}</div>
          <h2>{program.name}</h2>
          <p className="dim" style={{ fontSize: '0.9rem' }}>
            {manifest.name}
          </p>

          <p className="play__desc">{program.description ?? manifest.description}</p>

          <ParamSummary manifest={manifest} params={config} />

          <div style={{ textAlign: 'left', marginBottom: '1.75rem' }}>
            <LevelLadder manifest={manifest} config={config} />
          </div>

          <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'center' }}>
            <button type="button" className="btn" onClick={() => void navigate(byProgram ? '/trenink' : '/')}>
              Zpět
            </button>
            <button type="button" className="btn btn--primary" onClick={() => setStage('playing')}>
              Spustit trénink
            </button>
          </div>

          <p className="field__help" style={{ marginTop: '1.25rem' }}>
            Posaď se tak, abys měl monitor zhruba na délku paže a oči ve výšce jeho středu.
            Po celou dobu sledování se dívej na terčík uprostřed.
          </p>
        </div>
      </div>
    </div>
  )
}
